import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { emailValidator } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentMembersDataFacade } from '@core/tellent-logic';
import { TellentComponentsNotificationService } from '@core/tellent-util';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  HeadingComponent,
  InputComponent,
  LabelComponent,
} from '@core/user-interface-legacy';

interface TellentEditMemberEmailForm {
  email: FormControl<string>;
}

type EditMemberEmailDialogData = {
  memberId: string;
};

@Component({
  selector: 'edit-member-email-dialog',
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    ButtonComponent,
    ReactiveFormsModule,
    InputComponent,
    HeadingComponent,
    LabelComponent,
    TranslatePipe,
  ],
  templateUrl: './edit-member-email-dialog.component.html',
})
export class EditMemberEmailDialogComponent
  implements DialogComponentI<EditMemberEmailDialogData, null>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject<DialogRef<EditMemberEmailDialogComponent>>(DialogRef);
  private readonly notificationService = inject(TellentComponentsNotificationService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly membersDataFacade = inject(TellentMembersDataFacade);

  public form = this.formBuilder.nonNullable.group<TellentEditMemberEmailForm>({
    email: new FormControl<string>('', {
      validators: [Validators.required, emailValidator],
      nonNullable: true,
    }),
  });

  public memberGuid: string;
  public errorMessage$ = signal<string>('');

  public ngOnInit(): void {
    this.subscribeAndSuccessNotification();
    this.subscribeAndHandleError();
  }

  public submit(): void {
    this.dispatchFormUpdate();
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public setDialogData(data: EditMemberEmailDialogData): void {
    this.memberGuid = data.memberId;
  }

  private dispatchFormUpdate(): void {
    this.membersDataFacade.updateMember({
      email: this.form.value.email!,
      guid: this.memberGuid,
    });
  }

  private subscribeAndSuccessNotification(): void {
    this.membersDataFacade.updateMemberSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dialogRef.close();
        this.notificationService.success(
          this.i18n.translate(
            'tlc.members.edit_member.account_tab.section.email.dialog.success_toast',
            { email: this.form.value.email },
          ),
        );
      });
  }

  private subscribeAndHandleError(): void {
    this.membersDataFacade.updateAbilitiesFail$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(error => {
        const emailError = error.payload.error.errors.email[0];
        const emailErrorMessage = this.i18n.translate(
          'tlc.members.edit_member.account_tab.section.email.errors.already_taken',
        );

        if (emailError === 'has already been taken') this.errorMessage$.set(emailErrorMessage);
      });
  }
}
